import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlmContract,
  useRemovePlmContractMutation,
  usePlmContractsQuery,
  usePlmContractListSubSubscription,
} from '../../../../types/generated-types';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Add, Delete, Edit, Visibility } from '@mui/icons-material';
import { Notifier } from '../../../system/services/notificationManager';
import { useInjectableComponents } from '../../../system/services/injectableComponentsManager';
import { BottomMenuItem } from '../../../system/services/injectableComponentsManager';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';

type MyPlmContract = any;

export const PlmContractList = () => {
  const navigate = useNavigate();
  const { can } = useAuthorizer();
  const [plmContracts, setPlmContracts] = useState<MyPlmContract[]>([]);

  const {
    data: myPlmContracts,
    loading,
    error,
  } = usePlmContractsQuery({
    fetchPolicy: 'network-only',
  });

  const [deletePlmContract] = useRemovePlmContractMutation();

  useEffect(() => {
    setPlmContracts(
      myPlmContracts?.plmContracts
        ? (myPlmContracts.plmContracts as any[])
        : [],
    );
  }, [myPlmContracts]);

  usePlmContractListSubSubscription({
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (error) {
      Notifier.error('There was a problem fetching the PLM contracts: ', error);
    }
  }, [error]);

  const handleRemovePlmContract = useCallback(
    async (id: string) => {
      if (!can('delete', 'PlmContract')) {
        Notifier.error('You do not have permission to delete PLM contracts');
        return;
      }

      try {
        await deletePlmContract({
          variables: {
            id,
          },
        });

        Notifier.success('PLM contract removed');
      } catch (e) {
        Notifier.error('Error removing PLM contract');
        console.error(e);
      }
    },
    [deletePlmContract, can],
  );

  const { setContextMenuItems, setTitle, setTitlePath } =
    useInjectableComponents();

  const isSmall = useIsSmallScreen();

  useEffect(() => {
    const items: BottomMenuItem[] = [];

    if (can('create', 'PlmContract')) {
      items.push({
        id: 'add-plmContract-menu-item',
        label: isSmall
          ? 'Add PLM Contract'
          : 'Add Peak Load Management Contract',
        icon: <Add fontSize="small" />,
        navTarget: '/plmContracts/add-plmContract',
      });
    }

    setContextMenuItems(items.length > 0 ? [{ items }] : undefined);

    return () => {
      setContextMenuItems(undefined);
    };
  }, [setContextMenuItems, can]);

  React.useEffect(() => {
    setTitle(isSmall ? 'PLM Contracts' : 'Peak Load Management Contracts');
    setTitlePath('/plmContracts');
    return () => {
      setTitle(undefined);
      setTitlePath(undefined);
    };
  }, [setTitle, setTitlePath, isSmall]);

  const columns = React.useMemo<MRT_ColumnDef<MyPlmContract>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 100,
        maxSize: 300,
      },
      {
        accessorKey: 'contractId',
        header: 'Contract ID',
        size: 100,
        maxSize: 300,
      },
      {
        accessorKey: 'program',
        header: 'Program',
        size: 100,
        maxSize: 300,
        Cell: ({ row }) => {
          const program = row.original.program;
          if (program) {
            return `${program.name} (${program.partner?.name || 'Unknown partner'})`;
          }
          return row.original.programId || 'No Program';
        },
      },
      {
        accessorKey: 'properties',
        header: 'Properties',
        size: 100,
        maxSize: 300,
        Cell: ({ row }) => {
          const properties = row.original.properties;
          if (properties && properties.length > 0) {
            const propertyTitles = properties
              .map((prop: any) => prop.title)
              .join(', ');
            return propertyTitles.length > 50
              ? `${propertyTitles.substring(0, 50)}...`
              : propertyTitles;
          }
          return row.original.propertyIds?.length > 0
            ? `${row.original.propertyIds.length} properties`
            : 'No Properties';
        },
      },
      {
        accessorKey: 'minimumReductionKW',
        header: 'Min Reduction (kW)',
        size: 150,
        maxSize: 200,
      },
      {
        accessorKey: 'maximumEventsPerSeason',
        header: 'Max Events/Season',
        size: 150,
        maxSize: 200,
      },
      {
        accessorKey: 'baselineMethod',
        header: 'Baseline Method',
        size: 125,
        maxSize: 200,
      },
      {
        accessorKey: 'incentiveDescription',
        header: 'Incentive',
        size: 100,
        maxSize: 200,
        Cell: ({ cell }) => {
          const incentive = cell.getValue<string>();
          return incentive || '-';
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 100,
        maxSize: 200,
      },
      {
        accessorKey: 'notificationEmail',
        header: 'Notification Email',
        size: 150,
        maxSize: 200,
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 120,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: '0px' }}>
            <Tooltip title="View">
              <IconButton
                onClick={() =>
                  navigate(`/plmContracts/${row.original._id}/view-plmContract`)
                }
              >
                <Visibility />
              </IconButton>
            </Tooltip>
            {can('update', 'PlmContract') && (
              <Tooltip title="Edit">
                <IconButton
                  onClick={() =>
                    navigate(
                      `/plmContracts/${row.original._id}/edit-plmContract`,
                    )
                  }
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
            {can('delete', 'PlmContract') && (
              <Tooltip title="Delete">
                <IconButton
                  onClick={() =>
                    row.original._id &&
                    handleRemovePlmContract(row.original._id)
                  }
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [navigate, handleRemovePlmContract, can],
  );

  if (loading) {
    return (
      <Box
        component="div"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="100%"
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Box>
          <Typography variant="h4" color="secondary">
            Preparing your data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box width={'100%'}>
      <MaterialReactTable
        columns={columns}
        data={plmContracts}
        state={{
          isLoading: loading,
          columnVisibility: {
            contractId: false,
            maximumEventsPerSeason: false,
            notificationEmail: false,
          },
        }}
        enableColumnFilters={false}
        enableColumnActions={false}
        enableColumnResizing={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableRowActions={false}
        enableRowSelection={false}
        enableTopToolbar={false}
        muiTableBodyRowProps={{ hover: false }}
        muiTableHeadCellProps={{
          sx: {
            fontWeight: 'bold',
            fontSize: '14px',
            pp: '0.5rem',
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: '14px',
            p: '0.5rem',
          },
        }}
        layoutMode="semantic"
      />
    </Box>
  );
};
